"use client";

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { GroupedVirtualList, GroupedVirtualListRef } from "@ospweb/sdk-ui";
import log from "logbeacon";
import { cn } from "utils";
import { AlertItem } from "../../TimeLineItem/AlertItem";
import { transformAgentReport } from "../../../360Reports/utils/reportTimeline";
import {
  ReportData,
  ReportItem,
} from "../../../360Reports/ReportItem/ReportItem";
import { TrackerReportItem } from "../../TimeLineItem/TrackerReportItem";
import Translations from "@translate/components/CoPilot/CopilotAgent/EdggyAgent/EdggyTimeLine/EdggyTimeLine";
import { I18nUtil } from "@utils/i18n";
import { useIsPc } from "@/src/hooks";
import { EmptyState } from "@/src/components/Empty";
import CardLoading from "@/src/components/Chat/Cards/CardLoading";
import {
  AgentReport,
  AgentReportLevelEnum,
  type Agent,
} from "@/src/features/api/memberApi";
import { useOnboardingData } from "@/src/components/Portfolio/Onboarding/useOnboardingData";
import { groupByDate, formatDateGroup } from "@/src/utils";
import { TAItems } from "@/src/components";

/**
 * 条件插入选项
 */
export interface ConditionalInsertOptions {
  position?: "top" | "after" | "before";
  matcher?: (
    item: AgentReport,
    index: number,
    groupKey: string,
    groupIndex: number,
  ) => boolean;
  searchGroups?: number;
}

export interface EdggyTimeLineRef {
  insertItem: (item: AgentReport, options?: ConditionalInsertOptions) => void;
  insertItems: (
    items: AgentReport[],
    options?: ConditionalInsertOptions,
  ) => void;
  scrollToTop: (smooth?: boolean) => void;
  updateItemHeight: (
    itemKey: string,
    height: number,
    isExpanded?: boolean,
  ) => void;
  recalculateHeights: () => void;
}

export interface EdggyTimeLineProps {
  agentId?: string;
  onChartClick: (value: {
    question?: string;
    title?: string;
    desc?: string;
    id?: string;
    link?: string;
    type?: string;
  }) => void;
  data: AgentReport[];
  hasNextPage: boolean;
  isLoading: boolean;
  isFetching?: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}
const I18n = I18nUtil.createScoped(Translations);

export const EdggyTimeLine = forwardRef<EdggyTimeLineRef, EdggyTimeLineProps>(
  (
    {
      onChartClick,
      data,
      hasNextPage,
      isLoading,
      isFetching,
      isFetchingNextPage,
      fetchNextPage,
      agentId: _agentId,
    },
    ref,
  ) => {
    const [expandedItemKeys, setExpandedItemKeys] = useState<Set<string>>(
      new Set(),
    );
    const [isPC] = useIsPc();
    const groupedVirtualListRef =
      useRef<GroupedVirtualListRef<AgentReport>>(null);
    const { apiAgentsData: agentList } = useOnboardingData(false, false);
    const agentInfoById = useCallback(
      (agentId: string) => {
        const agent = agentList?.filter((agent: Agent) => agent.id === agentId);
        return agent?.[0] || {};
      },
      [agentList],
    );
    /**
     * 将扁平数组转换为分组数据
     * 根据 created 时间戳按日期分组（Today, Yesterday, 或日期格式）
     */

    const groupedData = useMemo(() => {
      // let dailyReportData: AgentReport;
      // const dailyReportId = _dailyReport?.agent_report?.id;
      const filteredData = data;

      // 对 REPORT_360 类型的数据进行转换处理，确保数据格式兼容
      const processedData = filteredData.map((item) => {
        if (item.type === "REPORT_360") {
          const transformed = transformAgentReport(item);
          return transformed ?? item;
        }
        return item;
      });

      const allData = [...processedData];
      log.debug("[EdggyTimeLine] allData ===", JSON.stringify(allData));
      log.debug("[EdggyTimeLine] allData length ===", allData.length);
      // if (_dailyReport) {
      //   if (_dailyReport.status === "GENERATABLE") {
      //     // 如果 status 为 GENERATABLE，在 Today 分组开头插入空数据
      //     dailyReportData = {
      //       id: "daily-report" + Date.now(),
      //       agent_id: agentId ?? "",
      //       content: {
      //         total_cumulative_pnl:
      //           _dailyReport.total_cumulative_pnl?.toString() ?? "",
      //         total_cumulative_pnl_percent:
      //           _dailyReport.total_cumulative_pnl_percent?.toString() ?? "",
      //         today_cumulative_pnl:
      //           _dailyReport.today_cumulative_pnl?.toString() ?? "",
      //         today_cumulative_pnl_percent:
      //           _dailyReport.today_cumulative_pnl_percent?.toString() ?? "",
      //         summary: "",
      //       },
      //       created: Date.now(),
      //       level: undefined,
      //       type: "DAILY_REPORT",
      //       source_agent_ids: [],
      //       categories: [],
      //     } as AgentReport;
      //   } else {
      //     // 如果 status 不为 GENERATABLE，从 _dailyReport 中取数据，在 Today 分组开头插入
      //     dailyReportData = {
      //       id: _dailyReport.agent_report?.id ?? "",
      //       agent_id: agentId ?? "",
      //       type: "DAILY_REPORT",
      //       content: {
      //         total_cumulative_pnl:
      //           _dailyReport.total_cumulative_pnl?.toString() ?? "",
      //         total_cumulative_pnl_percent:
      //           _dailyReport.total_cumulative_pnl_percent?.toString() ?? "",
      //         today_cumulative_pnl:
      //           _dailyReport.today_cumulative_pnl?.toString() ?? "",
      //         today_cumulative_pnl_percent:
      //           _dailyReport.today_cumulative_pnl_percent?.toString() ?? "",
      //         summary: _dailyReport.agent_report?.content?.summary ?? "",
      //       },
      //       created: _dailyReport.agent_report?.created ?? Date.now(),
      //       level: undefined,
      //       source_agent_ids: _dailyReport.agent_report?.source_agent_ids,
      //       categories: [],
      //     };
      //   }
      //   allData?.unshift(dailyReportData);
      // }
      const groupedData = groupByDate<AgentReport>(
        allData,
        "created",
        "YYYY-MM-DD",
        formatDateGroup,
      );
      log.debug("[EdggyTimeLine] groupedData ===", JSON.stringify(groupedData));
      log.debug(
        "[EdggyTimeLine] groupedData length ===",
        Object.keys(groupedData).length,
      );
      log.debug(
        "[EdggyTimeLine] groupedData keys ===",
        Object.keys(groupedData),
      );
      return groupedData;
    }, [data]);

    // useEffect(() => {
    //   if (!_dailyReport) {
    //     return;
    //   }
    //   log.debug(
    //     "[文案]groupedData",
    //     _dailyReport,
    //     localStorage.getItem("is_daily_report_loaded"),
    //     `${_dailyReport?.agent_report?.id ?? ""}_${agentId ?? ""}`,
    //   );
    //   if (
    //     localStorage.getItem("is_daily_report_loaded") === "true" &&
    //     _dailyReport?.status !== "GENERATABLE"
    //   ) {
    //     const itemId = `${_dailyReport?.agent_report?.id ?? ""}_${agentId ?? ""}`;
    //     if (itemId) {
    //       setExpandedItemKeys((prev) => new Set(prev).add(itemId));
    //     }
    //     localStorage.removeItem("is_daily_report_loaded");
    //   }
    // }, [_dailyReport, agentId]);

    //  暴露给父组件的方法

    useImperativeHandle(
      ref,
      () => ({
        insertItem: (item: AgentReport, options?: ConditionalInsertOptions) => {
          log.debug("[EdggyAgent][ws] insertItem ===", JSON.stringify(item));

          // 如果没有提供选项，使用默认行为
          if (!options) {
            groupedVirtualListRef.current?.insertItem(item);
            return;
          }

          // 使用条件插入
          groupedVirtualListRef.current?.insertItem(item, {
            position: options.position,
            matcher: options.matcher,
            searchGroups: options.searchGroups,
          });
        },
        insertItems: (
          items: AgentReport[],
          options?: ConditionalInsertOptions,
        ) => {
          log.debug("[EdggyAgent][ws] insertItems ===", JSON.stringify(items));
          // 如果没有提供选项，使用默认行为
          if (!options) {
            groupedVirtualListRef.current?.insertItems(items);
            return;
          }

          // 使用条件插入
          groupedVirtualListRef.current?.insertItems(items, {
            position: options.position,
            matcher: options.matcher,
            searchGroups: options.searchGroups,
          });
        },
        scrollToTop: (smooth?: boolean) => {
          groupedVirtualListRef.current?.scrollToTop(smooth);
        },
        updateItemHeight: (
          itemKey: string,
          height: number,
          isExpanded?: boolean,
        ) => {
          groupedVirtualListRef.current?.updateItemHeight(
            itemKey,
            height,
            isExpanded,
          );
        },
        recalculateHeights: () => {
          groupedVirtualListRef.current?.recalculateHeights();
        },
      }),
      [],
    );

    /**
     * 处理滚动到底部，加载更多数据
     */
    const handleEndReached = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
        log.debug("[EdggyTimeLine] onEndReached, fetching next page");
        fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const renderLoading = useMemo(() => {
      if (!hasNextPage || !isFetchingNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }, [hasNextPage, isFetchingNextPage]);

    const renderNoMore = useMemo(() => {
      if (hasNextPage) {
        return null;
      }
      return (
        <div className="flex items-center justify-center py-4 pb-20">
          <span className="body_s text-content_quaternary">
            {I18n.t("No more data")}
          </span>
        </div>
      );
    }, [hasNextPage]);

    /**
     * 动态计算列表项高度
     * 根据数据内容计算不同的高度
     */
    const getItemHeight = useCallback(
      (item: AgentReport, isExpanded: boolean) => {
        if (isExpanded) {
          // 展开高度 = 基础高度 + 表格高度
          return 300; // 增加展开高度估算值
        }
        // 根据不同类型返回更准确的高度估算
        if (item.type === "DAILY_REPORT") {
          return 68; // TrackerReportItem 基础高度
        }
        // AlertItem 基础高度（包括 padding、icon、标题、时间等）
        return 112;
      },
      [],
    );

    const renderHeader = useCallback((groupKey: string) => {
      return (
        <div className="flex items-center h-5 bg-background_solid_secondary w-full">
          <span className="body_s text-content_tertiary">{groupKey}</span>
        </div>
      );
    }, []);

    const renderTimelineItem = useCallback(
      (
        item: AgentReport,
        _index: number,
        isExpanded: boolean,
        toggleExpand: () => void,
      ) => {
        log.debug("[EdggyTimeLine] item ===", JSON.stringify(item));

        if (item.type === "DAILY_REPORT") {
          return (
            <TrackerReportItem
              onChartClick={onChartClick}
              summary={item.content?.summary ?? ""}
              created={item.created || Date.now()}
              id={item.id}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
            />
          );
        } else if (item.type === "TA") {
          return (
            <TAItems
              onChartClick={onChartClick}
              item={item}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              isPC={isPC}
            />
          );
        } else if (item.type === "REPORT_360") {
          // 数据已在 groupedData 中通过 transformAgentReport 转换，确保类型正确
          return (
            <ReportItem
              reportData={item as ReportData}
              viewFullInModal={true}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              buttonText={I18n.t("New Report")}
            />
          );
        }
        return (
          <AlertItem
            agentName={agentInfoById(item.agent_id ?? "")?.name ?? ""}
            agentId={item.agent_id ?? ""}
            id={item.id ?? ""}
            impact={item.content?.impact ?? ""}
            summary={item.content?.description ?? ""}
            title={item.content?.title ?? ""}
            category={item.categories?.[0] ?? ""}
            level={item.level as AgentReportLevelEnum}
            created={item.created || Date.now()}
            isExpanded={isExpanded ?? false}
            toggleExpand={toggleExpand}
            onChartClick={onChartClick}
            sourceLink={item.source_link ?? ""}
            assets={item.assets}
          />
        );
      },
      [onChartClick, isPC, agentInfoById],
    );

    const handleGetItemKey = useCallback(
      (item: AgentReport) => `${item?.id ?? ""}_${item?.agent_id ?? ""}`,
      [],
    );

    const handleGetItemHeight = useCallback(
      (item: AgentReport) => getItemHeight(item, false),
      [getItemHeight],
    );

    const handleItemToggle = useCallback((key: string, isExpanded: boolean) => {
      setExpandedItemKeys((prev) => {
        const next = new Set(prev);
        if (isExpanded) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    }, []);

    // 初始加载状态：没有数据且正在加载时显示全屏 loading
    // 使用 isFetching 判断重新请求的情况，isLoading 判断首次加载
    const isInitialLoading = (isLoading || isFetching) && data.length === 0;

    if (isInitialLoading) {
      return (
        <div className="flex flex-col w-full h-[calc(100vh-268px)] items-center justify-center">
          <CardLoading className="min-h-18 w-full" />
        </div>
      );
    }

    // 空状态：没有数据且不在加载时显示空状态提示
    if (data.length === 0 && !isLoading) {
      return (
        <div className="flex flex-col w-full h-[calc(100vh-268px)] items-center justify-center">
          <EmptyState
            title={I18n.t("No Smart Signals Yet")}
            description={I18n.t(
              "Check other agents, I’ll alert you when this one finds something worth your attention.",
            )}
            className="w-full max-w-[320px]"
          />
        </div>
      );
    }

    // 有数据时显示列表
    return (
      <div
        className="flex flex-col relative"
        data-tracking-scope="edggy-time-line"
      >
        <GroupedVirtualList
          ref={groupedVirtualListRef}
          data={groupedData}
          getItemKey={handleGetItemKey}
          itemGap={isPC ? 16 : 12}
          groupGap={8}
          groupToItemGap={8}
          getItemHeight={handleGetItemHeight}
          enableAutoHeightDetection={true}
          groupHeaderHeight={24}
          stickyTopOffset={0}
          hasNextPage={hasNextPage ?? false}
          onEndReached={handleEndReached}
          onEndReachedThreshold={40}
          renderItem={renderTimelineItem}
          renderGroupHeader={renderHeader}
          renderLoading={renderLoading}
          renderNoMore={renderNoMore}
          expandedItemKeys={expandedItemKeys}
          onItemToggle={handleItemToggle}
          className={cn("md:h-[calc(100vh-208px)] h-[calc(100vh-248px)]")}
        />
      </div>
    );
  },
);

EdggyTimeLine.displayName = "EdggyTimeLine";
